import type { State } from './reducer'
import type { ActionsNetwork } from 'shared/store/actions'
import type { ActionPrivilegesCoupons } from 'shared/store/homepage'
import { actionNetworkError, fetchPageData } from 'shared/store/actions'
import endpoints from 'shared/store/endpoints'
import type { Coupon, CouponForm, KingdomPoints } from 'shared/types'
import type { Dispatch } from 'redux'
import type { RootState } from 'shared/store/reducers'
import { couponOrigin, errors } from 'shared/utils'
import { ws } from 'shared/store/ws'
import type { ActionGetDeals } from 'shared/store/deals'
import type { ActionDashboardDataResponse } from 'shared/store/kingdom/dashboard'
import type { ActionUntransformPointsSuccess } from 'shared/store/kingdom/points'

const { page, wallet } = endpoints.kingdom.privileges
type GetState = () => RootState

export type ActionPrivilegesDataResponse = {
    type: 'PAGE_DATA_PRIVILEGES_RESPONSE',
    data: State
}

type ActionPrivilegesLogout = {
    type: 'LOGOUT'
}

type ActionPrivilegesSigninSend = {
    type: 'SIGNIN_SEND_FORM_SUCCESS'
}

type ActionPrivilegesSigninSocial = {
    type: 'SOCIAL_PENDING'
}

type ActionPrivilegesSignupSend = {
    type: 'SIGNUP_SEND_FORM_SUCCESS'
}

export type ActionAddCouponSuccess = {
    type: 'PRIVILEGES_ADD_COUPON_SUCCESS',
    coupons: Array<Coupon>,
    pointInfos?: KingdomPoints,
    promoCode?: string
}

type ActionAddCouponError = {
    type: 'PRIVILEGES_ADD_COUPON_ERROR',
    addCouponError: string,
    promoCode?: string
}

type ActionResetAddCouponSuccess = {
    type: 'PRIVILEGES_RESET_ADD_COUPON_SUCCESS'
}

type ActionPrivilegesError = {
    type: 'PRIVILEGES_ERROR',
    privilegesError: boolean
}

const fetchPrivilegesPageData = () =>
    fetchPageData(page, 'PAGE_DATA_PRIVILEGES_RESPONSE')

const actionError = (privilegesError: boolean): ActionPrivilegesError => ({
    type: 'PRIVILEGES_ERROR',
    privilegesError
})

export const handleCouponMessageError = (error: { response: any }) => {
    const { response } = error
    console.error('Error adding coupon :', response)
    let message = 'form.error.default'
    if (response) {
        switch (response.data) {
            case errors.OPERATION_INVALID_DATE:
                message = 'form.coupon.error.operation.no.longer.available'
                break
            case errors.OPERATION_NOT_AVAILABLE:
                message = 'form.coupon.error.operation.not.available'
                break
            case errors.OPERATION_TOTAL_LIMIT_REACHED:
                message = 'form.coupon.error.operation.total.limit.reached'
                break
            case errors.OPERATION_INVALID_ORDER_TYPE:
                message = 'form.coupon.error.operation.invalid.order.type'
                break
            case errors.OPERATION_INVALID_RESTAURANT:
                message = 'form.coupon.error.operation.invalid.restaurant'
                break
            case errors.PROMOTION_NOT_AVAILABLE_IN_RESTAURANT:
                message =
                    'form.coupon.error.promotion.not.available.in.restaurant'
                break
            case errors.PRODUCT_NOT_AVAILABLE_IN_RESTAURANT:
                message =
                    'form.coupon.error.product.not.available.in.restaurant'
                break
            case errors.NOT_ALLOWED:
            case errors.CODE_INVALID:
                message = 'form.coupon.error.invalid'
                break
            case errors.CODE_EXPIRED:
                message = 'form.coupon.error.expired'
                break
            case errors.OPERATION_USER_LIMIT_REACHED:
            case errors.CODE_ALREADY_USED:
                message = 'form.coupon.error.already.used'
                break
            case errors.COUPON_NOT_AVAILABLE_IN_RESTAURANT:
                message = 'form.coupon.error.coupon.not.available.in.restaurant'
                break
            case errors.COUPON_ALREADY_USED:
            case errors.ALREADY_LOGGED:
                message = 'form.coupon.error.already.coupon.used'
                break
            case errors.OPERATION_USER_NOT_ELIGIBLE:
            case errors.CODE_NOT_ELIGIBLE:
                message = 'form.coupon.error.not.eligible'
                break
            case errors.NOT_AUTHENTICATED:
                message = 'form.coupon.error.not.authenticated'
                break
            case errors.LEAP_YEAR_OPERATION_TOTAL_LIMIT_REACHED:
                message = 'form.coupon.error.leap.year.total.limit.reached'
                break
            case errors.CODE_NOT_AVAILABLE:
                message = 'form.coupon.error.not.available'
                break
            case errors.NOT_ELIGIBLE_FOR_STUDENT_BLASON:
                message = 'form.coupon.error.student.not.eligible'
                break
            /*case errors.INVALID_PICKUP_TYPE:
                message = 'form.coupon.error.drive.only'
                break*/
            default:
                break
        }
    }
    return message
}
const addCoupon = (form: CouponForm) => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        try {
            const response = await ws.post(endpoints.ecommerce.coupons, form)
            const { coupon, pointInfos } = response.data
            const { coupons } = getState().kingdomPrivilegesPageState
            if (!!coupon && coupon.origin !== couponOrigin.IMPERSONAL_COUPON) {
                coupons.unshift(coupon)
            }
            dispatch(actionAddCouponSuccess(coupons, pointInfos, form.code))
            return coupon
        } catch (error) {
            const message = handleCouponMessageError(error)
            dispatch(actionAddCouponError(message, form.code))
        }
    }
}

const actionAddCouponSuccess = (
    coupons: Array<Coupon>,
    pointInfos?: KingdomPoints,
    promoCode?: string
): ActionAddCouponSuccess => ({
    type: 'PRIVILEGES_ADD_COUPON_SUCCESS',
    coupons,
    pointInfos,
    promoCode
})

const actionAddCouponError = (
    addCouponError: string,
    promoCode?: string
): ActionAddCouponError => ({
    type: 'PRIVILEGES_ADD_COUPON_ERROR',
    addCouponError,
    promoCode
})

const resetAddCouponSuccess = (): ActionResetAddCouponSuccess => ({
    type: 'PRIVILEGES_RESET_ADD_COUPON_SUCCESS'
})

const getWallet = (code: string) => async (dispatch: Dispatch<any>) => {
    try {
        const response = await ws.get(wallet, {
            params: {
                code
            }
        })
        dispatch(actionError(false))
        return response.data && response.data.wallet
    } catch (error) {
        console.error('Error getting wallet in privileges page', error)
        switch (error) {
            case errors.NETWORK:
                dispatch(
                    actionNetworkError({
                        type: errors.NETWORK
                    })
                )
                break
            case errors.TIMEOUT:
                dispatch(
                    actionNetworkError({
                        type: errors.TIMEOUT
                    })
                )
                break
            default:
                dispatch(actionError(error))
                break
        }
        return false
    }
}

export {
    actionError,
    fetchPrivilegesPageData,
    actionAddCouponError,
    addCoupon,
    resetAddCouponSuccess,
    getWallet
}

export type Action =
    | ActionPrivilegesDataResponse
    | ActionPrivilegesLogout
    | ActionPrivilegesSigninSend
    | ActionPrivilegesSigninSocial
    | ActionPrivilegesSignupSend
    | ActionPrivilegesError
    | ActionAddCouponSuccess
    | ActionAddCouponError
    | ActionResetAddCouponSuccess
    | ActionsNetwork
    | ActionPrivilegesCoupons
    | ActionGetDeals
    | ActionDashboardDataResponse
    | ActionUntransformPointsSuccess
